/**
 * A person's chats with the staff assistant, as Claude's sidebar (Gary, 18 Oct): Starred, Projects (each opens to its chats),
 * then Recents grouped Today, Yesterday, Previous 7 days and so on, with a search box. Star, move to a project,
 * rename and delete show on the row being pointed at (always on a touch screen). Stars and projects are shared
 * with CMB Chats. Used by the Ask page's sidebar and the side panel's history.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../Toast';
import Modal from '../Modal';
import type { AssistantRole } from '../../config/assistant';
import {
  chatGroup, createProject, deleteConversation, deleteProject, listConversations, listProjects, moveConversation, renameConversation,
  renameProject, starConversation, type AskProject, type ConversationSummary,
} from './askApi';

interface AskChatListProps {
  activeId: string | null;
  onOpen: (id: string | null) => void;
  /** Changes whenever the open chat may have been created or renamed, so the list reloads. */
  refreshKey?: string | null;
  /** Hands the loaded list up, so the page can show the open chat's title. */
  onLoaded?: (chats: ConversationSummary[]) => void;
  /** Starts a new chat that goes in this project (Gary, 18 Oct). Not offered where not given. */
  onNewInProject?: (project: AskProject) => void;
  /** The page's projects, so it can name the one a new chat goes in. */
  onProjects?: (projects: AskProject[]) => void;
  /** Whose chats to list (Gary, 1 Nov). Ted unless given. */
  assistant?: AssistantRole;
}

export default function AskChatList({ activeId, onOpen, refreshKey, onLoaded, onNewInProject, onProjects, assistant = 'ted' }: AskChatListProps) {
  const { supabase, user } = useAuth();
  const { showToast } = useToast();
  const [chats, setChats] = useState<ConversationSummary[] | null>(null);
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState<string | null>(null);
  const [editText, setEditText] = useState('');
  const [confirmDelete, setConfirmDelete] = useState<ConversationSummary | null>(null);
  const [failed, setFailed] = useState(false);
  const [projects, setProjects] = useState<AskProject[]>([]);
  const [openProjects, setOpenProjects] = useState<string[]>([]);
  const [newProject, setNewProject] = useState<string | null>(null);
  const [editingProject, setEditingProject] = useState<string | null>(null);
  const [moving, setMoving] = useState<ConversationSummary | null>(null);
  const [moveName, setMoveName] = useState('');
  const [deletingProject, setDeletingProject] = useState<AskProject | null>(null);
  // Escape unmounts the rename box, which can fire its blur; this stops that blur saving.
  const cancelRename = useRef(false);

  const userId = user?.id ?? null;
  useEffect(() => {
    if (!userId) return;
    listConversations(supabase, userId, 200, assistant)
      .then((c) => { setChats(c); setFailed(false); onLoaded?.(c); })
      .catch(() => { setChats([]); setFailed(true); });
    listProjects(supabase).then((p) => { setProjects(p); onProjects?.(p); }).catch(() => setProjects([]));
    // onLoaded is a callback from the parent; reloading on its identity would loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supabase, userId, refreshKey, assistant]);

  const term = search.trim().toLowerCase();
  const shown = useMemo(() => (chats ?? []).filter((c) => !term || (c.title ?? '').toLowerCase().includes(term)), [chats, term]);
  const starred = shown.filter((c) => c.starred_at).sort((a, b) => (b.starred_at ?? '').localeCompare(a.starred_at ?? ''));
  const groups = useMemo(() => {
    const out: { label: string; items: ConversationSummary[] }[] = [];
    for (const c of shown.filter((x) => !x.starred_at && !x.project_id)) {
      const label = chatGroup(c.updated_at);
      const last = out[out.length - 1];
      if (last?.label === label) last.items.push(c);
      else out.push({ label, items: [c] });
    }
    return out;
  }, [shown]);

  const patch = (id: string, change: Partial<ConversationSummary>) => setChats((all) => (all ?? []).map((c) => (c.id === id ? { ...c, ...change } : c)));

  const toggleStar = async (c: ConversationSummary) => {
    const on = !c.starred_at;
    patch(c.id, { starred_at: on ? new Date().toISOString() : null });
    try { await starConversation(supabase, c.id, on); } catch { patch(c.id, { starred_at: c.starred_at }); showToast('Could not save the star', 'error'); }
  };

  const moveTo = async (c: ConversationSummary, projectId: string | null) => {
    setMoving(null);
    patch(c.id, { project_id: projectId });
    try { await moveConversation(supabase, c.id, projectId); } catch { patch(c.id, { project_id: c.project_id }); showToast('Could not move the chat', 'error'); }
  };

  const addProject = async (name: string): Promise<AskProject | null> => {
    if (!name.trim()) return null;
    try {
      const p = await createProject(supabase, name);
      setProjects((all) => [...all, p].sort((a, b) => a.name.localeCompare(b.name)));
      setOpenProjects((o) => [...o, p.id]);
      return p;
    } catch { showToast('Could not create the project', 'error'); return null; }
  };

  const saveProjectName = async (id: string, name: string) => {
    setEditingProject(null);
    if (!name.trim()) return;
    setProjects((all) => all.map((p) => (p.id === id ? { ...p, name: name.trim() } : p)));
    try { await renameProject(supabase, id, name); } catch { showToast('Could not rename the project', 'error'); }
  };

  const removeProject = async () => {
    const target = deletingProject;
    setDeletingProject(null);
    if (!target) return;
    try {
      await deleteProject(supabase, target.id);
      setProjects((all) => all.filter((p) => p.id !== target.id));
      setChats((all) => (all ?? []).map((c) => (c.project_id === target.id ? { ...c, project_id: null } : c)));
      showToast('Project deleted', 'success');
    } catch { showToast('Could not delete the project', 'error'); }
  };

  const startRename = (c: ConversationSummary) => {
    cancelRename.current = false;
    setEditing(c.id);
    setEditText(c.title ?? '');
  };

  const saveTitle = async (id: string) => {
    const title = editText.trim();
    setEditing(null);
    if (cancelRename.current || !title) return;
    try {
      await renameConversation(supabase, id, title);
      const next = (chats ?? []).map((c) => (c.id === id ? { ...c, title } : c));
      setChats(next);
      onLoaded?.(next);
    } catch {
      showToast('Could not rename the chat', 'error');
    }
  };

  const remove = async () => {
    const target = confirmDelete;
    setConfirmDelete(null);
    if (!target) return;
    try {
      await deleteConversation(supabase, target.id);
      setChats((all) => (all ?? []).filter((c) => c.id !== target.id));
      if (target.id === activeId) onOpen(null);
      showToast('Chat deleted', 'success');
    } catch {
      showToast('Could not delete the chat', 'error');
    }
  };

  function chatRow(c: ConversationSummary) {
    if (editing === c.id) {
      return (
        <input
          key={c.id}
          className="form-input ask-list-rename"
          value={editText}
          maxLength={200}
          autoFocus
          aria-label="Chat name"
          onChange={(e) => setEditText(e.target.value)}
          onBlur={() => void saveTitle(c.id)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') { e.preventDefault(); void saveTitle(c.id); }
            if (e.key === 'Escape') { e.stopPropagation(); cancelRename.current = true; setEditing(null); }
          }}
        />
      );
    }
    const act = (title: string, glyph: string, run: () => void, danger = false) => (
      <span className={`action-icon${danger ? ' action-icon--danger' : ''}`} title={title} aria-label={title} role="button" tabIndex={0}
        onClick={run} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); run(); } }}>{glyph}</span>
    );
    return (
      <div key={c.id} className={`ask-list-row ${c.id === activeId ? 'active' : ''}`}>
        <button type="button" className="ask-list-open" onClick={() => onOpen(c.id)} title={c.title || 'Untitled chat'}>
          {c.title || 'Untitled chat'}
        </button>
        <span className="ask-list-actions">
          {act(c.starred_at ? 'Unstar' : 'Star', c.starred_at ? '★' : '☆', () => void toggleStar(c))}
          {act('Move to project', '📁', () => setMoving(c))}
          {act('Rename', '✏️', () => startRename(c))}
          {act('Delete', '🗑️', () => setConfirmDelete(c), true)}
        </span>
      </div>
    );
  }

  return (
    <div className="ask-list">
      <input
        type="search"
        className="form-input ask-list-search"
        placeholder="Search chats"
        aria-label="Search chats"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />

      <div className="ask-list-scroll">
        {chats === null ? (
          <div className="ask-list-empty"><div className="spinner" /></div>
        ) : (
          <>
            {starred.length > 0 && (
              <div className="ask-list-group">
                <div className="ask-list-label">Starred</div>
                {starred.map((c) => chatRow(c))}
              </div>
            )}

            <div className="ask-list-group">
              <div className="ask-list-label ask-list-label--row">
                <span>Projects</span>
                <span className="action-icon" title="New project" aria-label="New project" role="button" tabIndex={0}
                  onClick={() => setNewProject('')} onKeyDown={(e) => { if (e.key === 'Enter') setNewProject(''); }}>➕</span>
              </div>
              {newProject !== null && (
                <input className="form-input ask-list-rename" placeholder="Project name" aria-label="Project name" maxLength={80} autoFocus value={newProject}
                  onChange={(e) => setNewProject(e.target.value)}
                  onBlur={() => { const n = newProject; setNewProject(null); void addProject(n); }}
                  onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); if (e.key === 'Escape') setNewProject(null); }} />
              )}
              {projects.length === 0 && newProject === null && <div className="ask-list-empty ask-list-empty--small">Keep related chats together: add a project with ➕.</div>}
              {projects.map((p) => {
                const inIt = shown.filter((c) => c.project_id === p.id);
                if (term && !inIt.length && !p.name.toLowerCase().includes(term)) return null;
                const open = openProjects.includes(p.id) || (!!term && inIt.length > 0);
                return (
                  <div key={p.id}>
                    {editingProject === p.id ? (
                      <input className="form-input ask-list-rename" defaultValue={p.name} maxLength={80} autoFocus aria-label="Project name"
                        onBlur={(e) => void saveProjectName(p.id, e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); if (e.key === 'Escape') setEditingProject(null); }} />
                    ) : (
                      <div className="ask-list-row ask-list-row--project">
                        <button type="button" className="ask-list-open" aria-expanded={open}
                          onClick={() => setOpenProjects((o) => (o.includes(p.id) ? o.filter((x) => x !== p.id) : [...o, p.id]))}>
                          <span className="ask-list-chev">{open ? '▾' : '▸'}</span> 📁 {p.name} <span className="ask-list-count">{inIt.length || ''}</span>
                        </button>
                        <span className="ask-list-actions">
                          {onNewInProject && (
                            <span className="action-icon" title="New chat in this project" aria-label="New chat in this project" role="button" tabIndex={0} onClick={() => onNewInProject(p)}
                              onKeyDown={(e) => { if (e.key === 'Enter') onNewInProject(p); }}>➕</span>
                          )}
                          <span className="action-icon" title="Rename project" aria-label="Rename project" role="button" tabIndex={0} onClick={() => setEditingProject(p.id)}
                            onKeyDown={(e) => { if (e.key === 'Enter') setEditingProject(p.id); }}>✏️</span>
                          <span className="action-icon action-icon--danger" title="Delete project" aria-label="Delete project" role="button" tabIndex={0} onClick={() => setDeletingProject(p)}
                            onKeyDown={(e) => { if (e.key === 'Enter') setDeletingProject(p); }}>🗑️</span>
                        </span>
                      </div>
                    )}
                    {open && (inIt.length
                      ? <div className="ask-list-inproject">{inIt.map((c) => chatRow(c))}</div>
                      : <div className="ask-list-empty ask-list-empty--small ask-list-inproject">No chats yet. Move one here with 📁.</div>)}
                  </div>
                );
              })}
            </div>

            {groups.length === 0 && starred.length === 0 ? (
              <div className="ask-list-empty">{failed ? 'Could not load your chats. Reopen to try again.' : search ? 'No chats match.' : 'No chats yet.'}</div>
            ) : groups.map((g) => (
              <div key={g.label} className="ask-list-group">
                <div className="ask-list-label">{g.label}</div>
                {g.items.map((c) => chatRow(c))}
              </div>
            ))}
          </>
        )}
      </div>

      {moving && (
        <Modal title="Move to project" size="sm" onClose={() => setMoving(null)}
          footer={<button type="button" className="btn btn-secondary" onClick={() => setMoving(null)}>Cancel</button>}>
          <div className="ask-move-list">
            {projects.map((p) => (
              <button key={p.id} type="button" className={`btn btn-secondary ask-move-option${p.id === moving.project_id ? ' active' : ''}`} onClick={() => void moveTo(moving, p.id)}>📁 {p.name}</button>
            ))}
            {moving.project_id && <button type="button" className="btn btn-secondary ask-move-option" onClick={() => void moveTo(moving, null)}>No project (back to Recents)</button>}
            <form className="ask-move-new" onSubmit={(e) => { e.preventDefault(); const target = moving; void addProject(moveName).then((p) => { setMoveName(''); if (p) void moveTo(target, p.id); }); }}>
              <input className="form-input" placeholder="New project name" aria-label="New project name" maxLength={80} value={moveName} onChange={(e) => setMoveName(e.target.value)} />
              <button type="submit" className="btn btn-primary" disabled={!moveName.trim()}>Create and move</button>
            </form>
          </div>
        </Modal>
      )}

      {deletingProject && (
        <Modal title="Delete this project?" size="sm" onClose={() => setDeletingProject(null)}
          footer={
            <>
              <button type="button" className="btn btn-secondary" onClick={() => setDeletingProject(null)}>Cancel</button>
              <button type="button" className="btn btn-danger" onClick={() => void removeProject()}>Delete</button>
            </>
          }>
          <p className="m-0">“{deletingProject.name}” will be removed. Its chats go back to Recents; nothing is deleted.</p>
        </Modal>
      )}

      {confirmDelete && (
        <Modal
          title="Delete this chat?"
          size="sm"
          onClose={() => setConfirmDelete(null)}
          footer={
            <>
              <button type="button" className="btn btn-secondary" onClick={() => setConfirmDelete(null)}>Cancel</button>
              <button type="button" className="btn btn-danger" onClick={() => void remove()}>Delete</button>
            </>
          }
        >
          <p className="m-0">
            “{confirmDelete.title || 'Untitled chat'}” will be removed for good. Any query you logged on it stays with the team.
          </p>
        </Modal>
      )}
    </div>
  );
}
